import { useMutation, useQueryClient } from "react-query";
import { useCallback, useEffect, useState } from "react";
import {
  useUnlinkKakao,
  useUpdateUserData,
} from "@utils/api-requests/StrivacityReqeusts";
import {
  KakaoUnlinkErrorText,
  PhoneNumberNotMatchText,
  StrivacityLinkErrorText,
  StrivacityUpdateErrorText,
} from "@utils/lanugauge-settings/static-texts/TranslatedTexts";
import { STRIVACITY_USER_DATA_QUERY_KEY } from "@utils/react-query/queryKey";

const useKakaoLink = (userData, userProfile) => {
  const kakaoId = userProfile?.kakaoId;

  const queryClient = useQueryClient();

  const [isKakaoAccount, setIsKakaoAccount] = useState(false);
  const [kakaoAlert, setKakaoAlert] = useState(<></>);
  const [strivacityAlert, setStrivacityAlert] = useState(<></>);
  const [unlinkModalOpen, setUnlinkModalOpen] = useState(false);
  const [unlinkSuccess, setUnlinkSuccess] = useState(false);

  // NOTE: 1. [KAKAO] Unlink user
  const handleUnlinkSuccess = (data) => {
    if (data?.response?.data?.error?.msg === "NotRegisteredUserException") {
      // CASE: Error: No user kakao id in kakao DB
      setIsKakaoAccount(false);
      setKakaoAlert("");

      return;
    }

    if (data?.status === 200) {
      // CASE: unlinked successfully
      setKakaoAlert("");
      setIsKakaoAccount(false);

      queryClient.refetchQueries({
        queryKey: [STRIVACITY_USER_DATA_QUERY_KEY],
      });
    } else if (data.message === "Network Error") {
      return false;
    } else {
      // ! error while unlinking user from kakao -> rollback unlinking in strivacity
      setKakaoAlert(<KakaoUnlinkErrorText />);
      setUnlinkSuccess(false);
      setUnlinkModalOpen(false);

      updateKakaoId({
        body: { kakaoId: kakaoId ? kakaoId : userData?.kakaoID },
      });
    }
  };

  const { mutate: unlinkUser, isLoading: unlinking } = useMutation(
    useUnlinkKakao,
    {
      onSuccess: handleUnlinkSuccess,
    },
  );

  // NOTE: 2. [STRIVACITY] Update Kakao ID
  const {
    mutate: updateKakaoId,
    isLoading,
    isError: strivacityError,
  } = useMutation(useUpdateUserData, {
    onSuccess: (data) => {
      if (data?.status === 200) {
        if (data?.data?.kakaoId !== null) {
          // CASE: when the user is linked to strivacity
          setIsKakaoAccount(true);
          setStrivacityAlert("");

          queryClient.refetchQueries({
            queryKey: [STRIVACITY_USER_DATA_QUERY_KEY],
          });
        } else {
          // CASE: when the user is unlinked from strivacity -> kakao unlink
          setUnlinkSuccess(true);
          setStrivacityAlert("");
          unlinkUser(kakaoId ? kakaoId : userData?.kakaoID);
        }
      } else if (data.message === "Network Error") {
        return false;
      } else {
        if (!isKakaoAccount) {
          unlinkUser(data?.body?.kakaoId);
          setIsKakaoAccount(false);
        }
        // ! error while updating user data -> unlink kakao
        setStrivacityAlert(<StrivacityUpdateErrorText />);
        setUnlinkModalOpen(false);
        setUnlinkSuccess(false);
      }
    },
  });

  // NOTE: 3. [KAKAO] Login Success - link kakaoId
  const loginSuccess = useCallback(
    (data) => {
      const formattedPhoneNumber = data.profile.kakao_account.phone_number
        ?.replaceAll(" ", "")
        ?.replaceAll("-", "");

      if (userData?.identifiers?.phone === formattedPhoneNumber) {
        // CASE: when user's phone number is matched -> add kakaoId to strivacity
        try {
          updateKakaoId({
            body: { kakaoId: JSON.stringify(data.profile.id) },
          });
        } catch (e) {
          // ! error while strivacity link -> roll back link kakao
          console.log(e, "error");

          setIsKakaoAccount(false);

          unlinkUser(data.profile.id);

          setStrivacityAlert(<StrivacityLinkErrorText />);
        }
      } else {
        // CASE: when user's phone number is NOT matched -> roll back link kakao
        unlinkUser(data.profile.id);

        setStrivacityAlert(<PhoneNumberNotMatchText />);
      }
    },
    [unlinkUser, updateKakaoId, userData?.identifiers?.phone],
  );

  useEffect(() => {
    if (kakaoId) {
      setIsKakaoAccount(true);
    } else {
      setIsKakaoAccount(false);
    }
  }, [kakaoId]);

  return {
    setUnlinkSuccess,
    isKakaoAccount,
    kakaoAlert,
    setKakaoAlert,
    strivacityAlert,
    unlinkModalOpen,
    unlinkSuccess,
    unlinking,
    isLoading,
    strivacityError,
    unlinkUser,
    updateKakaoId,
    loginSuccess,
    setUnlinkModalOpen,
  };
};

export default useKakaoLink;
